import React, { useEffect, useState } from 'react';
import { bool, func, object } from 'prop-types';
import styled from 'styled-components';
import { Drawer, notification, Divider } from 'antd';
import { Field, Formik, Form } from 'formik';
import moment from 'moment';
import { sortBy, get, orderBy, map, find, toNumber, omit, includes, filter, isString, isEmpty, head } from 'lodash';
import { ConfirmFooterButtons, FormikSelect, Text, CompanyGroupsSelect, Row, Icon } from '../../../common';
import { storesKind, monthOptions } from '../../../../utils/misc';
import CREATE_POLL from '../../../../queries/CREATE_POLL';
import UPDATE_POLL from '../../../../queries/UPDATE_POLL';

const styleNotification = {
    borderRadius: 8,
    height: 42,
    padding: '9px 12px',
    top: 45
};

export const CustomRadioButton = styled.div`
    height: 19px;
    margin-right: 5px;
    margin-top: 10px;
    margin-bottom: 10px;
    width: 19px;
    border-radius: 50%;
    background-color: #fff;
    border: solid 1px #d9d9d9;
`;

export const CustomRadioButtonIn = styled.div`
    margin: 2px;
    height: 13px;
    width: 13px;
    border-radius: 50%;
    background-color: #a4c735;
`;

const DrawerPoll = ({ visible, onClose, pollSelected, isCreate, session, client, refreshData }) => {
    const sessionSalesChannel = orderBy(map(get(session, 'salesChannels'), ({ name, store, id, kind }) => ({ label: `${store} - ${name}`, value: id, kind })), ['label'], ['asc']);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [selectedCompanyGroup, onChangeSelectedCompanyGroup] = useState({});

    const defaultFlag = get(head(filter(sessionSalesChannel, (store) => includes(map(get(pollSelected, 'associatedStore'), 'idStore'), toNumber(get(store, 'value'))))), 'kind');

    const handleSelectedCompanyGroup = (value, setFieldValue) => {
        onChangeSelectedCompanyGroup(value);
        defaultFlag !== value && setFieldValue('associatedStore', []);
    };

    const showNotification = (isSuccess) => {
        setTimeout(() => onClose(), 1000);

        const error = {
            msg: `Hubo un error al ${isCreate ? 'crear' : 'editar'} encuesta.`,
            color: '#BD2C00',
            icon: 'alert-circle',
            bgColor: '#F7E6E1',
            borderColor: '#BD2C00'
        };

        const success = {
            msg: `La encuesta se ${isCreate ? 'creó' : 'editó'} correctamente.`,
            color: '#22A889',
            icon: 'checkmark-circle',
            bgColor: '#E6F5F1',
            borderColor: '#B6DFD6'
        };

        const { msg, bgColor, icon, color, borderColor } = isSuccess ? success : error;

        notification.open({
            message: (<Row align='center'>
                <Icon name={icon} size={24} color={color} />
                <Divider type='vertical' style={{ background: color, height: '1.5em', margin: '0px 16px 0px 14px' }} />
                <Text text={msg} size={12} color='#323A44' />
            </Row>),
            style: { ...styleNotification, backgroundColor: bgColor, border: `1px solid ${borderColor}` }
        });
    };

    const initialValues = () => {
        const questions = get(pollSelected, 'questions');
        const activationRange = head(get(pollSelected, 'activationRange'));
        const init = get(activationRange, 'init');
        const end = get(activationRange, 'end');
        const storeIds = map(get(pollSelected, 'associatedStore'), 'idStore');
        const stores = filter(sessionSalesChannel, (store) => includes(storeIds, toNumber(get(store, 'value'))));

        return {
            ...pollSelected,
            questions: sortBy(questions, 'orden'),
            associatedStore: stores,
            activationRange: isEmpty(activationRange) ? [moment(), moment()] : [moment(init), moment(end)],
            monthEvaluate: find(monthOptions, { value: get(pollSelected, 'monthEvaluate') })
        };
    };

    useEffect(() => {
        setLoading(false);
        setSuccess(false);
    }, []);

    const formatActivationRange = (values) => {
        const activationRange = get(values, 'activationRange');
        return [{ ...activationRange, init: get(activationRange, 'init').format('YYYY-MM-DD'), end: get(activationRange, 'end').format('YYYY-MM-DD') }];
    };

    const getFlagStore = (idStore) => {
        const store = find(sessionSalesChannel, { value: idStore });
        const flag = toNumber(get(find(storesKind, { name: get(store, 'kind') }), 'id'));
        return flag || 0;
    };

    const removeOptionsIds = (options) => {
        return map(options, (option, index) => {
            return { ...omit(option, ['idOption']), orden: index + 1 };
        });
    };

    const removeQuestionIds = (questions) => {
        return map(questions, (question, index) => {
            const options = get(question, 'options');
            const questionWithoutId = omit(question, ['idQuestion']);

            return { ...questionWithoutId, options: removeOptionsIds(options), orden: index + 1 };
        });
    };

    const validateIdsAndTypename = (items, key) => {
        return map(items, (item) => {
            const updatedItem = omit(item, '__typename');
            return isString(get(updatedItem, key)) ? omit(updatedItem, key) : updatedItem;
        });
    };

    const validateQuestions = (questions) => {
        const validatedQuestions = validateIdsAndTypename(questions, 'idQuestion');

        return map(validatedQuestions, (question, index) => {
            const options = get(question, 'options');
            return { ...question, options: validateIdsAndTypename(options, 'idOption'), orden: index + 1 };
        });
    };

    const submit = async (values) => {
        const questions = get(values, 'questions');
        const stores = map(get(values, 'associatedStore'), ({ value }) => {
            return {
                idStore: toNumber(value),
                amount: 100,
                status: 1,
                flag: getFlagStore(value)
            };
        });

        const formattedValues = {
            ...values,
            associatedStore: stores,
            activationRange: formatActivationRange(values),
            monthEvaluate: get(values, 'monthEvaluate.value'),
            userCreate: get(session, 'email'),
            questions: isCreate ? removeQuestionIds(questions) : validateQuestions(questions),
            status: isCreate ? 2 : get(values, 'status'),
            userUpdate: get(session, 'email')
        };

        setLoading(true);
        const response = await client.mutate({
            mutation: isCreate ? CREATE_POLL : UPDATE_POLL,
            variables: {
                input: {
                    ...omit(formattedValues, ['dateCreate', 'dateUpdate', '__typename'])
                }
            },
            refetchQueries: ['getPolls']
        });

        const action = isCreate ? 'createPoll' : 'updatePoll';
        const isSuccess = get(response, `data.${action}.internalCode`) === '200';
        setSuccess(isSuccess);
        showNotification(isSuccess);
        setLoading(false);
        refreshData();
    };

    const salesChannelFilteredOptionsByKind = options => {
        return filter(options, ({ kind }) => includes(selectedCompanyGroup, kind));
    };

    return <Drawer
        title='Activar sala'
        placement='right'
        onClose={onClose}
        visible={visible}
        width={350}
        destroyOnClose
    >
        <Formik initialValues={isCreate ? {} : initialValues()} onSubmit={submit}>
            {({ setFieldValue }) => {
                return <Form style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CompanyGroupsSelect onChange={({ value }) => handleSelectedCompanyGroup(value, setFieldValue)} initialValue={{ label: defaultFlag || 'Jumbo', value: defaultFlag || 'Jumbo' }} />
                    <Field component={FormikSelect} label='Tienda' name='associatedStore' options={salesChannelFilteredOptionsByKind(sessionSalesChannel)} />
                    <ConfirmFooterButtons justify='center' width={150} withZindex htmlType='submit' confirmDisabled={success} onCancel={onClose} loading={loading} />
                </Form>;
            }}
        </Formik>
    </Drawer>;
};

DrawerPoll.propTypes = {
    visible: bool,
    onClose: func,
    pollSelected: object,
    isCreate: bool,
    session: object,
    client: object,
    refreshData: func
};

export default DrawerPoll;
